
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/store/auth-store';
import { resolveBranding } from '@/sections/public-preview/white-label';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { DraftPin, PinMarker, ThreadComment } from '@/sections/public-preview/pin-ui';
import { Link, useSearch, useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  ShareHeader,
  PeriodShareView,
  ShareUnavailable,
} from '@/sections/public-preview/period-share-view';
import {
  usePublicEmailPreviewControllerVerify,
  usePublicEmailPreviewControllerWhoami,
  usePublicEmailPreviewControllerGetPreview,
  usePublicEmailPreviewControllerGetSession,
  usePublicEmailPreviewControllerListComments,
  usePublicEmailPreviewControllerCreateComment,
  usePublicEmailPreviewControllerStartMagicLink,
  usePublicEmailPreviewControllerReviewerInvites,
} from '@/apis/backend/generated/email-preview-public/email-preview-public';

import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import RemoveIcon from '@mui/icons-material/Remove';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Button,
  Drawer,
  Avatar,
  Tooltip,
  Divider,
  Popover,
  Container,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';

type Search = { code?: string };

export const Route = createFileRoute('/p/$token')({
  component: PublicPreviewPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    code: typeof s.code === 'string' ? s.code : undefined,
  }),
});

const SESSION_STORAGE_KEY = 'nexmail_preview_session';

function readSession(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeSession(value: string | null) {
  try {
    if (value) localStorage.setItem(SESSION_STORAGE_KEY, value);
    else localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

function PublicPreviewPage() {
  const { t } = useTranslation('common');
  const { token } = Route.useParams();
  const { code } = useSearch({ from: '/p/$token' });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [sessionToken, setSessionToken] = useState<string | null>(() => readSession());
  const [emailInput, setEmailInput] = useState('');
  const [pinDraft, setPinDraft] = useState<{ xPercent: number; yPercent: number } | null>(null);
  const [draftBody, setDraftBody] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [threadAnchor, setThreadAnchor] = useState<HTMLElement | null>(null);
  const [threadReply, setThreadReply] = useState('');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);
  const [iframeHeight, setIframeHeight] = useState(800);
  const [zoom, setZoom] = useState(1);
  const measureIframe = useCallback(() => {
    const body = iframeRef.current?.contentDocument?.body;
    if (!body) return;
    const h = body.scrollHeight;
    if (h > 0) setIframeHeight(h);
  }, []);
  const clamp = (z: number) => Math.min(2, Math.max(0.25, z));
  const handleFit = useCallback(() => {
    const wrapWidth = scrollWrapRef.current?.clientWidth ?? 900;
    // native email frame width tracks the Container maxWidth=md (~ 900px).
    // Fit-to-width gives a slight padding so it doesn't kiss the gutters.
    setZoom(clamp(Math.min(1, (wrapWidth - 48) / 900)));
  }, []);

  const sessionHeader: Record<string, string> = sessionToken
    ? { 'x-preview-session': sessionToken }
    : {};

  const whoami = usePublicEmailPreviewControllerWhoami({
    query: {
      enabled: !!sessionToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
    request: { headers: sessionHeader },
  });

  // `undefined` is the new `params` argument: WP16 gave the public preview an
  // optional `?groupBy` for period shares, which shifted `options` along one.
  const preview = usePublicEmailPreviewControllerGetPreview(token, undefined, {
    query: {
      enabled: !!token,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    request: { headers: sessionHeader },
  });

  // Owner auto-pass: if no session, but a NexMail JWT is in scope, the backend
  // mints a session inline and returns it on the same response. Capture it.
  useEffect(() => {
    const minted = preview.data?.mintedSessionToken;
    if (minted && minted !== sessionToken) {
      writeSession(minted);
      setSessionToken(minted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.data?.mintedSessionToken]);

  const verifyMutation = usePublicEmailPreviewControllerVerify();
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<boolean>(() => !!code);
  // Verify the magic-link code by AWAITING mutateAsync's promise and applying
  // the result directly — NOT via mutate's onSuccess callback or the mutation's
  // observer state. Under React StrictMode the mutation observer is torn down
  // before the response lands, so callback/observer updates were silently
  // dropped: verify returned a valid session token that never reached
  // localStorage and the page spun forever. The promise settles regardless of
  // the observer lifecycle. The ref guard keeps it to one call per code value.
  const verifiedCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!code) {
      setVerifying(false);
      return;
    }
    if (verifiedCodeRef.current === code) return;
    verifiedCodeRef.current = code;
    setVerifying(true);
    verifyMutation
      .mutateAsync({ shareToken: token, data: { code } })
      .then(res => {
        writeSession(res.sessionToken);
        setSessionToken(res.sessionToken);
        setVerifyError(null);
        toast.success(t('publicPreview.signedIn', 'Signed in.'));
        navigate({ to: '/p/$token', params: { token }, search: {}, replace: true });
      })
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('publicPreview.signInLinkInvalid', 'Sign-in link is invalid or expired.');
        setVerifyError(message);
        toast.error(message);
        // Strip the dead code so a refresh doesn't loop on the same error.
        navigate({ to: '/p/$token', params: { token }, search: {}, replace: true });
      })
      .finally(() => setVerifying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (sessionToken) {
      void preview.refetch();
      void whoami.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const startMagicLink = usePublicEmailPreviewControllerStartMagicLink();
  const commentsQuery = usePublicEmailPreviewControllerListComments(token, {
    query: {
      enabled: !!sessionToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
    request: { headers: sessionHeader },
  });
  const createComment = usePublicEmailPreviewControllerCreateComment({
    request: { headers: sessionHeader },
  });

  // A period or plan link carries no `html` of its own, so "is this reviewer
  // signed in AND on this brand's allowlist" cannot be read off the payload the
  // way it can for a single email. This route answers 200 for exactly that
  // reviewer and 403 for everyone else, which is the question being asked.
  const isPeriodShare = preview.data ? preview.data.subjectType !== 'email' : false;
  const shareSession = usePublicEmailPreviewControllerGetSession(token, {
    query: {
      enabled: !!sessionToken && isPeriodShare,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
    request: { headers: sessionHeader },
  });

  const commentsByPin = useMemo(() => {
    const list = commentsQuery.data?.comments ?? [];
    return list.filter(c => c.xPercent != null && c.yPercent != null && !c.parentId);
  }, [commentsQuery.data]);

  const threadRoot = threadId
    ? (commentsQuery.data?.comments ?? []).find(c => c.id === threadId) ?? null
    : null;
  const threadReplies = (commentsQuery.data?.comments ?? [])
    .filter(c => threadId != null && c.parentId === threadId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const submitThreadReply = () => {
    const body = threadReply.trim();
    if (!body || !threadId || !sessionToken) return;
    createComment.mutate(
      { shareToken: token, data: { body, parentId: threadId } },
      {
        onSuccess: () => {
          setThreadReply('');
          void commentsQuery.refetch();
        },
        onError: () =>
          toast.error(t('publicPreview.couldNotSaveComment', 'Could not save comment')),
      },
    );
  };

  if (preview.isLoading || verifying || (!!sessionToken && isPeriodShare && shareSession.isLoading)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (preview.error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('publicPreview.unavailableTitle', 'This preview is no longer available')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('publicPreview.unavailableBody', 'The link may have been revoked, or the share token is invalid.')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const doc = preview.data;
  if (!doc) return null;

  // An agency's link carries their branding, not ours (WG-52). Resolved once
  // here so the sign-in card, the header and the period page cannot disagree
  // about whose page a reviewer is looking at.
  const branding = resolveBranding(doc.whiteLabel, doc.brandName);
  const isPeriod = doc.subjectType !== 'email';

  // A revoked or expired link answers 200 with `state` and nothing else, so it
  // gets a plain message rather than an error page (WG-53).
  if (doc.state !== 'active') {
    return <ShareUnavailable brandName={branding.name} />;
  }

  const isAuthed = Boolean(sessionToken && (isPeriod ? shareSession.isSuccess : doc.html));
  const signedInEmail = whoami.data?.email;
  const signedInButNoAccess = Boolean(sessionToken && (isPeriod ? shareSession.isError : !doc.html));

  const handleFrameClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isAuthed || !showComments) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
    setPinDraft({ xPercent, yPercent });
    setDraftBody('');
  };

  const handleSubmitComment = () => {
    if (!pinDraft || !draftBody.trim() || !sessionToken) return;
    createComment.mutate(
      {
        shareToken: token,
        data: {
          body: draftBody.trim(),
          xPercent: pinDraft.xPercent,
          yPercent: pinDraft.yPercent,
        },
      },
      {
        onSuccess: () => {
          setPinDraft(null);
          setDraftBody('');
          void commentsQuery.refetch();
          toast.success(t('publicPreview.commentAdded', 'Comment added'));
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
              ?.message;
          const text = Array.isArray(message) ? message.join(', ') : message;
          toast.error(text ?? t('publicPreview.couldNotSaveComment', 'Could not save comment'));
           
          console.error('[preview-comment] failed', err);
        },
      },
    );
  };

  const handleRequestMagicLink = () => {
    if (!emailInput.trim()) return;
    startMagicLink.mutate(
      { shareToken: token, data: { email: emailInput.trim() } },
      {
        onSuccess: () => {
          toast.success(t('publicPreview.magicLinkSent', 'If that email is on the reviewer list, a sign-in link has been sent.'));
          setEmailInput('');
        },
        onError: () => toast.error(t('publicPreview.couldNotSendSignInLink', 'Could not send sign-in link')),
      },
    );
  };

  const handleSignOut = () => {
    writeSession(null);
    setSessionToken(null);
    void preview.refetch();
    toast.success(t('publicPreview.signedOut', 'Signed out'));
  };

  // Signed in but not allowlisted for THIS share's client
  if (signedInButNoAccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }} elevation={2}>
          <Typography variant="overline" color="text.secondary">
            {branding.name}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, mb: 1 }}>
            {t('publicPreview.notInvitedTitle', "You're signed in, but not invited to this preview")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('publicPreview.notInvitedBodyPre', 'Signed in as')} <strong>{signedInEmail}</strong>. {t('publicPreview.notInvitedBodyPost', "Ask the sender to invite this email to comment on this brand's previews.")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {/* The cross-brand inbox is a NexMail surface by another name, so a
                white-labelled link does not offer it (WG-52). */}
            {branding.showNexmail && (
              <Button
                component={Link}
                to="/p"
                variant="contained"
                startIcon={<GridViewIcon fontSize="small" />}
                size="small"
              >
                {t('publicPreview.seeAllSharedWithYou', 'See all previews shared with you')}
              </Button>
            )}
            {!isAuthenticated && (
              <Button
                onClick={handleSignOut}
                startIcon={<LogoutIcon fontSize="small" />}
                size="small"
              >
                {t('publicPreview.signInAsDifferentEmail', 'Sign in as a different email')}
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!isAuthed) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }} elevation={2}>
          {branding.logoUrl && (
            <Box
              component="img"
              src={branding.logoUrl}
              alt=""
              sx={{ height: 32, maxWidth: 180, objectFit: 'contain', mb: 1.5, display: 'block' }}
            />
          )}
          <Typography variant="overline" color="text.secondary">
            {branding.name}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5, mb: 2 }}>
            {isPeriod
              ? t('publicPreview.signInTitlePeriod', 'Sign in to view this plan')
              : t('publicPreview.signInTitle', 'Sign in to view this email preview')}
          </Typography>
          {verifyError && (
            <Paper
              variant="outlined"
              sx={{ p: 1.5, mb: 2, borderColor: 'error.light', bgcolor: 'error.50' }}
            >
              <Typography variant="body2" color="error.main">
                {verifyError}
              </Typography>
            </Paper>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('publicPreview.signInHint', "Enter your email. If you've been invited to review previews from this brand, we'll send you a one-click sign-in link.")}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              type="email"
              placeholder={t('publicPreview.emailPlaceholder', 'you@company.com')}
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              fullWidth
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRequestMagicLink();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleRequestMagicLink}
              disabled={!emailInput.trim() || startMagicLink.isPending}
              fullWidth
              size="large"
            >
              {startMagicLink.isPending ? t('publicPreview.sending', 'Sending…') : t('publicPreview.sendSignInLink', 'Send sign-in link')}
            </Button>
          </Stack>
          {isAuthenticated && branding.showNexmail && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('publicPreview.ownerHint', "Heads-up: you're signed into nexmail. If you own this brand, refresh — owner sign-in is automatic.")}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  const allComments = commentsQuery.data?.comments ?? [];

  // The pieces of the header that mean the same thing whatever the link points
  // at. Kept as one expression so the period page and the single-email page
  // cannot drift into offering a reviewer different account controls.
  const accountActions = (
    <>
      {branding.showNexmail && (
        <Tooltip title={t('publicPreview.allSharedPreviews', 'All previews shared with you')}>
          <IconButton size="small" component={Link} to="/p">
            <GridViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {doc.allowReviewerInvites && (
        <Button
          size="small"
          startIcon={<PersonAddIcon fontSize="small" />}
          onClick={() => setInviteOpen(true)}
        >
          {t('publicPreview.invite', 'Invite')}
        </Button>
      )}
      <Tooltip
        title={
          isAuthenticated && branding.showNexmail
            ? t('publicPreview.signedInAsOwner', 'Signed in as {{email}} (owner)', { email: signedInEmail ?? t('publicPreview.yourNexMailAccount', 'your nexmail account') })
            : signedInEmail ?? t('publicPreview.signedIn', 'Signed in')
        }
      >
        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
          {(signedInEmail ?? '?').charAt(0).toUpperCase()}
        </Avatar>
      </Tooltip>
      {!isAuthenticated && (
        <Tooltip title={t('publicPreview.signInAsDifferentEmail', 'Sign in as a different email')}>
          <IconButton size="small" onClick={handleSignOut}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  // A period or plan link: the whole window on one page (WG-45).
  if (isPeriod && doc.period) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
        <ShareHeader
          name={branding.name}
          logoUrl={branding.logoUrl}
          accent={branding.accent}
          subtitle={doc.period.label}
          right={
            <Stack direction="row" spacing={1} alignItems="center">
              {accountActions}
            </Stack>
          }
        />
        <PeriodShareView
          token={token}
          period={doc.period}
          sessionHeader={sessionHeader}
          accent={branding.accent}
          onRefetchPreview={() => void preview.refetch()}
        />
        {inviteOpen && (
          <ReviewerInviteDialog
            token={token}
            sessionHeader={sessionHeader}
            onClose={() => setInviteOpen(false)}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <ShareHeader
        name={branding.name}
        logoUrl={branding.logoUrl}
        accent={branding.accent}
        subtitle={doc.subject}
        right={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={showComments ? t('publicPreview.hideComments', 'Hide comments') : t('publicPreview.showComments', 'Show comments')}>
              <IconButton size="small" onClick={() => setShowComments(v => !v)}>
                {showComments ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              {t('publicPreview.commentsCount', 'Comments ({{count}})', { count: allComments.length })}
            </Button>
            {accountActions}
          </Stack>
        }
      />

      <Box ref={scrollWrapRef} sx={{ overflow: 'auto', py: 4, px: 2 }}>
        <Box
          sx={{
            width: 900 * zoom,
            height: iframeHeight * zoom,
            mx: 'auto',
            position: 'relative',
            transition: 'width 100ms ease, height 100ms ease',
          }}
        >
          <Box
            sx={{
              width: 900,
              position: 'absolute',
              top: 0,
              left: 0,
              transformOrigin: 'top left',
              transform: `scale(${zoom})`,
            }}
          >
            <Paper
              elevation={2}
              sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2, bgcolor: '#fff' }}
            >
              <Box
                onClick={handleFrameClick}
                sx={{
                  position: 'relative',
                  cursor: showComments ? 'crosshair' : 'default',
                }}
              >
                <Box
                  component="iframe"
                  ref={iframeRef}
                  title={t('publicPreview.emailPreview', 'Email preview')}
                  srcDoc={doc.html}
                  sandbox="allow-same-origin"
                  onLoad={() => {
                    measureIframe();
                    window.setTimeout(measureIframe, 300);
                    window.setTimeout(measureIframe, 1200);
                  }}
                  sx={{
                    width: '100%',
                    height: iframeHeight,
                    border: 0,
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                />
                {showComments &&
                  commentsByPin.map(c => (
                    <PinMarker
                      key={c.id}
                      comment={c}
                      onOpen={el => {
                        setThreadId(c.id);
                        setThreadAnchor(el);
                        setThreadReply('');
                      }}
                    />
                  ))}
                {showComments && pinDraft && (
                  <DraftPin
                    x={pinDraft.xPercent}
                    y={pinDraft.yPercent}
                    value={draftBody}
                    onChange={setDraftBody}
                    onSubmit={handleSubmitComment}
                    onCancel={() => {
                      setPinDraft(null);
                      setDraftBody('');
                    }}
                    submitting={createComment.isPending}
                  />
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Threaded conversation for an existing comment pin */}
      <Popover
        open={Boolean(threadId && threadAnchor)}
        anchorEl={threadAnchor}
        onClose={() => {
          setThreadId(null);
          setThreadAnchor(null);
          setThreadReply('');
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: -1 } } }}
      >
        {threadRoot && (
          <Paper sx={{ width: 320, maxWidth: '90vw' }} elevation={6}>
            <Box sx={{ maxHeight: 300, overflowY: 'auto', p: 2 }}>
              <ThreadComment comment={threadRoot} />
              {threadReplies.length > 0 && (
                <Box sx={{ pl: 1.5, borderLeft: theme => `2px solid ${theme.palette.divider}` }}>
                  {threadReplies.map(r => (
                    <ThreadComment key={r.id} comment={r} />
                  ))}
                </Box>
              )}
            </Box>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  maxRows={4}
                  autoFocus
                  placeholder={t('publicPreview.replyPlaceholder', 'Reply…')}
                  value={threadReply}
                  onChange={e => setThreadReply(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitThreadReply();
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={submitThreadReply}
                  disabled={!threadReply.trim() || createComment.isPending}
                  aria-label={t('publicPreview.reply', 'Reply')}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        )}
      </Popover>

      {/* Floating zoom toolbar */}
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: 999,
          zIndex: 20,
        }}
      >
        <Tooltip title={t('publicPreview.zoomOut', 'Zoom out')}>
          <IconButton size="small" onClick={() => setZoom(z => clamp(z - 0.1))}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          onClick={() => setZoom(1)}
          sx={{ minWidth: 56, fontVariantNumeric: 'tabular-nums' }}
        >
          {Math.round(zoom * 100)}%
        </Button>
        <Tooltip title={t('publicPreview.zoomIn', 'Zoom in')}>
          <IconButton size="small" onClick={() => setZoom(z => clamp(z + 0.1))}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('publicPreview.fitToWidth', 'Fit to width')}>
          <IconButton size="small" onClick={handleFit}>
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 360, p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('publicPreview.comments', 'Comments')}
            </Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          {allComments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('publicPreview.noComments', 'No comments yet. Click anywhere on the email to leave one.')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {allComments.map(c => (
                <Stack key={c.id} spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {c.authorEmail}
                    {c.resolvedAt ? ` · ${t('publicPreview.resolved', 'resolved')}` : ''}
                  </Typography>
                  <Typography variant="body2">{c.body}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      {inviteOpen && (
        <ReviewerInviteDialog
          token={token}
          sessionHeader={sessionHeader}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </Box>
  );
}

function ReviewerInviteDialog({
  token,
  sessionHeader,
  onClose,
}: {
  token: string;
  sessionHeader: Record<string, string>;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState('');
  const [chips, setChips] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const reviewerInvites = usePublicEmailPreviewControllerReviewerInvites({
    request: { headers: sessionHeader },
    mutation: {
      onSuccess: data => {
        toast.success(t('publicPreview.invitesSent', 'Sent {{count}} invite', { count: data.sent }));
        onClose();
      },
      onError: () => toast.error(t('publicPreview.couldNotSendInvites', 'Could not send invites')),
    },
  });

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      toast.error(t('publicPreview.invalidEmail', '{{email}} is not a valid email', { email: v }));
      return;
    }
    if (!chips.includes(v)) setChips([...chips, v]);
    setDraft('');
  };

  const handleSend = () => {
    commit();
    const all = Array.from(new Set([...chips, draft.trim()].filter(isValidEmail)));
    if (all.length === 0) return toast.error(t('publicPreview.addAtLeastOneEmail', 'Add at least one email'));
    reviewerInvites.mutate({
      shareToken: token,
      data: { emails: all, message: message.trim() || undefined },
    });
  };

  return (
    <Drawer anchor="right" open onClose={onClose}>
      <Box sx={{ width: 380, p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('publicPreview.inviteOthers', 'Invite others')}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('publicPreview.inviteHint', "They'll get a one-click sign-in link via email. Same access you have.")}
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            mb: 1,
          }}
        >
          {chips.map(c => (
            <Chip key={c} size="small" label={c} onDelete={() => setChips(chips.filter(e => e !== c))} />
          ))}
          <TextField
            variant="standard"
            placeholder={chips.length ? '' : t('publicPreview.colleaguePlaceholder', 'colleague@company.com')}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Backspace' && !draft && chips.length) {
                setChips(chips.slice(0, -1));
              }
            }}
            onBlur={commit}
            InputProps={{ disableUnderline: true }}
            sx={{ flex: 1, minWidth: 140 }}
          />
        </Box>
        <TextField
          placeholder={t('publicPreview.addMessagePlaceholder', 'Add a message (optional)')}
          value={message}
          onChange={e => setMessage(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={reviewerInvites.isPending || (chips.length === 0 && !draft.trim())}
            startIcon={
              reviewerInvites.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
          >
            {t('publicPreview.sendInvites', 'Send invites')}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
